/**
 * Session 8 Block 4 · Operator Audit-Explorer
 * Platform-wide, cross-tenant audit filter with export.
 */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Search, Download, X } from "lucide-react";


function DetailModal({ event, onClose }) {
  if (!event) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center
                     justify-center p-4"
          onClick={onClose}
          data-testid="operator-audit-detail-modal">
      <div className="bg-background border border-border rounded-lg
                       max-w-3xl w-full max-h-[80vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-border flex justify-between">
          <h3 className="font-semibold text-lg">
            Audit-Event {event.id}
          </h3>
          <button onClick={onClose} data-testid="operator-audit-detail-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <pre className="p-4 text-xs whitespace-pre-wrap break-all">
          {JSON.stringify(event, null, 2)}
        </pre>
      </div>
    </div>
  );
}


export default function OperatorAudit() {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({
    tenant_slug: "", event_type: "", severity: "", search: "",
    from_ts: "", to_ts: "",
  });

  const load = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params.append(k, v);
    });
    params.append("page", page);
    params.append("page_size", "50");
    api.get(`/operator/audit/events?${params}`).then((r) => {
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    }).catch(() => toast.error("Laden fehlgeschlagen"));
  };

  useEffect(() => {
    api.get("/operator/audit/event-types").then(
      (r) => setTypes(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(load, [page]);

  const exportCsv = () => {
    const params = new URLSearchParams({ format: "csv" });
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params.append(k, v);
    });
    window.open(`/api/v1/operator/audit/export?${params}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="operator-audit-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Audit-Explorer</h1>
        <Button onClick={exportCsv}
                 data-testid="operator-audit-export-btn">
          <Download className="h-4 w-4 mr-2" /> CSV exportieren
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Filter</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input placeholder="Tenant-Slug (z.B. brimo)"
                  value={filters.tenant_slug}
                  onChange={(e) => setFilters({...filters,
                    tenant_slug: e.target.value})}
                  data-testid="operator-audit-filter-tenant" />
          <select className="rounded border border-input bg-background
                                px-3 py-2 text-sm"
                    value={filters.event_type}
                    onChange={(e) => setFilters({...filters,
                      event_type: e.target.value})}
                    data-testid="operator-audit-filter-event-type">
            <option value="">Alle Event-Typen</option>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className="rounded border border-input bg-background
                                px-3 py-2 text-sm"
                    value={filters.severity}
                    onChange={(e) => setFilters({...filters,
                      severity: e.target.value})}
                    data-testid="operator-audit-filter-severity">
            <option value="">Alle Schweregrade</option>
            <option value="warning">Warning</option>
            <option value="critical">Critical</option>
          </select>
          <Input placeholder="Von (ISO)" value={filters.from_ts}
                  onChange={(e) => setFilters({...filters,
                    from_ts: e.target.value})} />
          <Input placeholder="Bis (ISO)" value={filters.to_ts}
                  onChange={(e) => setFilters({...filters,
                    to_ts: e.target.value})} />
          <div className="flex gap-2">
            <Input placeholder="Suche"
                    value={filters.search}
                    onChange={(e) => setFilters({...filters,
                      search: e.target.value})}
                    data-testid="operator-audit-filter-search" />
            <Button onClick={() => { setPage(1); load(); }}
                    data-testid="operator-audit-apply-filter">
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            Ergebnisse ({total.toLocaleString("de-DE")})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {items.map((ev) => (
              <button key={ev.id}
                       className="w-full text-left p-3 hover:bg-accent
                                    grid grid-cols-6 gap-2 items-center text-sm"
                       onClick={() => setSelected(ev)}
                       data-testid={`operator-audit-row-${ev.id}`}>
                <span className="text-xs text-muted-foreground col-span-1
                                    truncate">
                  {ev.created_at}
                </span>
                <span className="font-mono text-xs col-span-1 truncate">
                  {ev.tenant_id?.slice(0, 8)}
                </span>
                <Badge variant="secondary" className="col-span-1 justify-self-start">
                  {ev.action}
                </Badge>
                <span className="text-xs col-span-1 truncate">
                  {ev.actor}
                </span>
                <span className="text-xs col-span-1 truncate">
                  {ev.resource_type}
                </span>
                <span className="text-xs col-span-1 truncate">
                  {ev.resource_id?.slice(0, 8)}
                </span>
              </button>
            ))}
            {!items.length && (
              <div className="p-8 text-center text-sm
                              text-muted-foreground">
                Keine Events gefunden.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <Button variant="outline" disabled={page <= 1}
                 onClick={() => setPage(page - 1)}>
          Zurück
        </Button>
        <span className="text-sm text-muted-foreground">Seite {page}</span>
        <Button variant="outline"
                 disabled={items.length < 50}
                 onClick={() => setPage(page + 1)}>
          Weiter
        </Button>
      </div>

      <DetailModal event={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
